import { IFilterCriteria } from '@forwardflow/features/campaign-filter/interfaces/campaign-filter.types'

/** The stages a launch goes through, in order. */
export type TLaunchStage = 'campaign' | 'numbers' | 'voice' | 'live'

export const LAUNCH_STAGES: { id: TLaunchStage; label: string }[] = [
   { id: 'campaign', label: 'Creating your campaign' },
   { id: 'numbers', label: 'Getting your local numbers' },
   { id: 'voice', label: 'Setting up the voice' },
   { id: 'live', label: 'Going live' },
]

/** How many numbers a first campaign gets. The activation modal calls 3 optimal. */
export const FIRST_CAMPAIGN_NUMBERS = 3

export interface ILaunchInput {
   campaignName: string
   criteria: IFilterCriteria[]
   campaignType: string[]
   leadTypes: string[]
   bufferTime: number
   areaCodes: string[]
   assistant: {
      assistantName: string
      voiceId: string
      avatar: string
      description: string
      previewUrl?: string
   }
}

export interface IAvailableNumber {
   phoneNumber: string
   [key: string]: unknown
}

export interface ILaunchDeps {
   createCampaign: (input: ILaunchInput) => Promise<{ id: string } | null | undefined>
   searchNumbers: (areaCode: string) => Promise<IAvailableNumber[]>
   reserveNumber: (campaignId: string, number: IAvailableNumber) => Promise<unknown>
   createAssistants: (campaignId: string, assistant: ILaunchInput['assistant']) => Promise<unknown>
   activate: (campaignId: string) => Promise<{ hasFailedPurchase?: boolean } | null | undefined>
   onStage?: (stage: TLaunchStage) => void
}

export interface ILaunchResult {
   campaignId: string
   reserved: string[]
   /** Area codes that had no numbers; the launch went on with what it found. */
   emptyAreaCodes: string[]
   /** The backend reported a failed purchase at activation. */
   hasFailedPurchase: boolean
}

export class LaunchError extends Error {
   constructor(
      public readonly stage: TLaunchStage,
      message: string,
      public readonly campaignId?: string
   ) {
      super(message)
      this.name = 'LaunchError'
   }
}

const unique = <T,>(items: T[]) => Array.from(new Set(items))

/**
 * Create, number, voice and activate the first campaign.
 *
 * Numbers come from the customer's area codes in order (owner's first), up
 * to `FIRST_CAMPAIGN_NUMBERS` in total; an empty area code is skipped, not
 * fatal. With no area code at all, any available number is used. The launch
 * fails only when no number can be reserved at all, or a call the backend
 * requires fails; the campaign id is carried on the error so the customer
 * can finish in the campaign console.
 */
export const launchFirstCampaign = async (
   input: ILaunchInput,
   deps: ILaunchDeps
): Promise<ILaunchResult> => {
   deps.onStage?.('campaign')
   let campaignId: string
   try {
      const created = await deps.createCampaign(input)
      if (!created?.id) throw new Error('no id')
      campaignId = String(created.id)
   } catch (error) {
      throw new LaunchError('campaign', "We couldn't create the campaign. Try again.")
   }

   deps.onStage?.('numbers')
   const reserved: string[] = []
   const emptyAreaCodes: string[] = []
   const codes = unique(input.areaCodes.filter(Boolean))
   const queue = codes.length ? codes : ['']
   for (const code of queue) {
      if (reserved.length >= FIRST_CAMPAIGN_NUMBERS) break
      let found: IAvailableNumber[] = []
      try {
         found = (await deps.searchNumbers(code)) || []
      } catch {
         found = []
      }
      const wanted = found
         .filter((n) => n?.phoneNumber && !reserved.includes(n.phoneNumber))
         .slice(0, FIRST_CAMPAIGN_NUMBERS - reserved.length)
      if (!wanted.length) {
         if (code) emptyAreaCodes.push(code)
         continue
      }
      for (const number of wanted) {
         try {
            await deps.reserveNumber(campaignId, number)
            reserved.push(number.phoneNumber)
         } catch {
            // One number failing to reserve is not the end; try the next.
         }
      }
   }
   // Nothing in the chosen codes: fall back to any number before giving up.
   if (!reserved.length && codes.length) {
      try {
         const any = ((await deps.searchNumbers('')) || []).filter((n) => n?.phoneNumber)
         for (const number of any.slice(0, FIRST_CAMPAIGN_NUMBERS)) {
            try {
               await deps.reserveNumber(campaignId, number)
               reserved.push(number.phoneNumber)
            } catch {
               // keep trying
            }
         }
      } catch {
         // handled below
      }
   }
   if (!reserved.length) {
      throw new LaunchError(
         'numbers',
         codes.length
            ? `No numbers are available in (${codes.join(', ')}) right now.`
            : 'No phone numbers are available right now.',
         campaignId
      )
   }

   deps.onStage?.('voice')
   try {
      await deps.createAssistants(campaignId, input.assistant)
   } catch {
      throw new LaunchError('voice', "We couldn't set up the voice. Try again.", campaignId)
   }

   deps.onStage?.('live')
   let hasFailedPurchase = false
   try {
      const result = await deps.activate(campaignId)
      hasFailedPurchase = Boolean(result?.hasFailedPurchase)
   } catch {
      throw new LaunchError('live', "We couldn't activate the campaign. Try again.", campaignId)
   }

   return { campaignId, reserved, emptyAreaCodes, hasFailedPurchase }
}
